import { useMemo } from 'react'
import { useNavigate, useParams, NavLink } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import { useChampionships } from '../features/command-center/useChampionships.js'
import {
  unfilledBodies, creationQueue, standingsDecided, registryGrades, ownerlessRecords,
  REASONS, REASON_COPY, GRADES, OWNER_REASONS, OWNER_REASON_COPY,
} from '../features/invisible/invisibleRecords.js'
/* The ONE authority on "who owns this record". Imported rather than
 * reimplemented so this surface and the Command Center's owner filter can never
 * disagree about the same record — see the note on Surface 5. */
import { ownerName } from '../lib/championships.js'
import snapshot from '../data/bodyRegistrySnapshot.json'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import styles from './InvisibleRecords.module.css'

/**
 * NO INVISIBLE RECORDS — the four sets that were files on one laptop.
 *
 * Brian: "Anything hidden, I need it deployed because hidden doesn't get
 * worked." Four sets came out of the 2026-10-11 lanes as a CSV, an HTML file and
 * a scratch JSON in `C:\Users\brian\.sr-scratch\`. A finding in a scratch
 * directory is not a finding anybody acts on, so this is the surface that
 * replaces them.
 *
 * ⚠️ READ LAYER. NOTHING HERE WRITES. Every count is computed at view time off
 * the same `useChampionships()` feed the Command Center reads, or off a dated
 * registry snapshot that says its own date. This page cannot become a second
 * source of truth because it stores nothing — and the one thing it does ship,
 * the registry, carries `meta.read_at` into the render so it can never be
 * mistaken for live. See read-only-dashboard.
 *
 * ⚠️ FOUR SURFACES, FOUR URLS, ONE SIDEBAR ENTRY. A rep clicks
 * Sidebar → Invisible Records and lands on the creation queue; the four tabs are
 * real links, so each surface can be sent to somebody. One nav entry rather than
 * four because the sidebar is a tool, not an index — the same call Body Coverage
 * made when it took one entry for a reconciliation the board could not do.
 *
 * ⚠️ NEVER A SILENT ZERO. Every panel on this page renders its reason for being
 * empty. An empty panel is a claim about the data and it is usually wrong: the
 * `NAMED` bucket is genuinely zero in this object and says so in words, which is
 * a different thing from a bucket that is zero because the query was wrong.
 *
 * ⚠️ NO MONEY FIGURE APPEARS ON THIS PAGE AND NONE MAY BE ADDED. RBAC makes
 * money manager-only; this surface is granted on CC_VIEW, which every role
 * holds. Adding an amount here would hand every role a figure its role is
 * supposed to gate. See RBAC.md.
 */

const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

/* ⚠️ THE ASSERTED PHRASES ARE CONSTANTS, NOT JSX TEXT. check-shipped-features
 * greps the BUILT bundle, and the JSX transform splits multi-line element text.
 * A named constant survives the build as one literal. Same reason
 * BodyCoverage.jsx does it. */
export const INVISIBLE_HEADLINE = 'hidden does not get worked'
export const INVISIBLE_RECONCILED =
  'Every record in the object is in exactly one row above, so nothing has been silently '
  + 'dropped from this count.'
export const INVISIBLE_RECONCILE_FAILED = 'RECONCILIATION FAILED.'
export const SNAPSHOT_NOT_LIVE =
  'This is a dated research snapshot, not a live read.'
export const UNVERIFIED_ON_ITS_FACE =
  'Every row below is UNVERIFIED. It is a work list, not a fact base.'
export const ABSENT_IS_NOT_ASSERTED =
  'A zero here does not assert that a body is absent.'
export const STANDINGS_IS_A_DIFFERENT_KIND =
  'Asking whether the season has ended is the wrong question for these.'

/* ⚠️ THE DOOR, AS A LITERAL. Brian: "we must be able to assign it." A surface
 * that shows the gap and does not say where the action is has shipped half the
 * ask, and this sentence is what check-shipped-features asserts against the
 * built bundle so the sentence cannot quietly disappear. */
/* ⚠️ "REP DROPDOWN", NOT "OWNER FILTER" — check-ownership-language bans `owner`
 * in anything a person reads, because a rep is ASSIGNED a record and does not
 * own it. The control's own first option still reads "All owners" (pre-existing,
 * and outside this lane); this sentence names the control by position so it
 * stays findable without repeating the word. */
export const NOREP_DOOR =
  'To work these in bulk: Command Center → the rep dropdown → “No stored CRM rep”.'

export const SURFACES = [
  { id: 'creation-queue', label: 'Creation queue' },
  { id: 'no-body', label: 'No governing body' },
  /* FIFTH, and placed third because it is the only one of the five whose rows
   * end in a write somebody can make today. The other four end in research. */
  { id: 'no-rep', label: 'No rep assigned' },
  { id: 'standings', label: 'Decided by standings' },
  { id: 'unverified', label: 'Unverified registry' },
]

/* ---------------------------------------------------------------------------
 * Shared furniture
 * ------------------------------------------------------------------------- */

/** An empty panel must say WHY it is empty. Never render a bare zero. */
function Empty({ children }) {
  return <p className={styles.empty} role="status">{children}</p>
}

function SnapshotStamp() {
  const m = snapshot.meta
  return (
    <p className={styles.stamp}>
      <b>{SNAPSHOT_NOT_LIVE}</b> The registry was built by the governing-body lane
      on <b>{m.read_at}</b> by probing object <code>{m.object}</code> in portal{' '}
      <code>{m.portal}</code> — {nf(m.total_records)} records read,{' '}
      {nf(m.live_records)} live, {nf(m.excluded)} excluded as retired, unresolved or
      test scaffolds. Source: <code>{m.source}</code>. There is no API behind the
      registry, so re-running <code>tools/gen-body-registry-snapshot.mjs</code> is the
      only thing that moves this date.
    </p>
  )
}

function AliasLimit() {
  return (
    <p className={styles.caveat}>
      <b>{ABSENT_IS_NOT_ASSERTED}</b> The lane&rsquo;s prober matched by <i>alias</i> with a
      jurisdiction guard, and <code>build_registry.py</code> drops the alias table from its
      output. So the live re-check on this page is an exact, case-folded label match, which
      is strictly weaker, and it is used in one direction only: it can show that a body
      carded absent <b>now holds records</b>, and it never claims one is absent. That matters
      because an exact match on a body&rsquo;s official name produces false zeros at roughly
      a 7% rate against this object — it is how School Sport Alberta sat at zero while
      holding 77 records, and how DoDEA was carded absent while every one of its records
      named it in <code>sr_conference</code> alone.
    </p>
  )
}

function Grade({ evidence }) {
  const g = GRADES[evidence]
  return (
    <span className={styles.grade} data-verified={g?.verified ? '1' : '0'}>
      {g ? g.short : evidence || 'UNGRADED'}
    </span>
  )
}

/* ---------------------------------------------------------------------------
 * SURFACE 1 — the creation queue
 * ------------------------------------------------------------------------- */
function CreationQueue({ records, complete }) {
  const q = useMemo(() => creationQueue(snapshot.bodies, records), [records])
  const t = q.totals

  return (
    <>
      <section className={`card ${styles.headline}`}>
        <div className={styles.big}>{nf(t.absent)}</div>
        <div className={styles.bigLabel}>
          governing bodies the object has <b>never recorded once</b>, with roughly{' '}
          <b>{nf(t.estTitles)}</b> titles estimated behind them. This is a{' '}
          <b>creation</b> queue, not a repair queue: &ldquo;present at zero&rdquo; came back
          empty when the lane measured it, and that was the finding.
        </div>
      </section>

      <SnapshotStamp />

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>It adds up, or it says so</h2>
        <table className={styles.recon}>
          <tbody>
            <tr>
              <td className={styles.n}>{nf(t.present)}</td>
              <td>registry bodies the object holds championships against</td>
            </tr>
            <tr data-flag="1">
              <td className={styles.n}>{nf(t.absent)}</td>
              <td>
                <b>never recorded.</b> The queue. <b>Fix: create the championships.</b>
              </td>
            </tr>
            <tr className={styles.sum}>
              <td className={styles.n}>{nf(t.registry)}</td>
              <td>bodies in the registry</td>
            </tr>
          </tbody>
        </table>
        {q.reconciled ? (
          <p className={styles.ok}>Reconciled. {INVISIBLE_RECONCILED}</p>
        ) : (
          <p className={styles.fail} role="alert">
            <strong>{INVISIBLE_RECONCILE_FAILED}</strong> The registry&rsquo;s own two states
            do not account for every body in it. Do not act on the rows below.
          </p>
        )}
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>
          The top of the queue — {nf(t.hsPrivate)} missing private and independent
          high-school associations
        </h2>
        <p className={styles.note}>
          The biggest structural hole in the whole registry, and the reason it is the top of
          the queue rather than a section further down. These are the associations that
          sanction private, parochial, charter and independent-prep state championships, and
          they sit beside the public association in every one of these states.
        </p>
        <div className={styles.chips}>
          {q.hsPrivateByJuris.map(([j, n]) => (
            <span key={j} className={styles.chip}><b>{nf(n)}</b> {j}</span>
          ))}
        </div>
        <div className={styles.rows}>
          {q.hsPrivate.map((b) => <QueueRow key={b.code} b={b} />)}
        </div>
      </section>

      {t.staleRows > 0 && (
        <section className={`card ${styles.section}`}>
          <h2 className={styles.h2}>
            ⚠️ {nf(t.staleRows)} of these are no longer absent
          </h2>
          <p className={styles.note}>
            The live feed now carries records under an exact label match for these bodies, so
            the snapshot is stale here. A fill lane has been writing to this object; that is
            expected and this row is how you can see it rather than being told a stale number.
            Re-run the registry before working the queue.
          </p>
          <div className={styles.rows}>
            {q.stale.map((b) => <QueueRow key={b.code} b={b} />)}
          </div>
        </section>
      )}

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>
          The whole queue, ranked — {nf(t.absent)} bodies
        </h2>
        <p className={styles.note}>
          Rank is the registry&rsquo;s own estimated title yield; it is not recomputed here.
          An estimate is labelled as one: <code>EST</code> on every row, because a number
          somebody could mistake for a holding is worse than no number.
          {!complete && ' The live feed is still loading, so the stale check below is not final.'}
        </p>
        <div className={styles.rows}>
          {q.rows.map((b) => <QueueRow key={b.code} b={b} />)}
        </div>
      </section>
      <AliasLimit />
    </>
  )
}

function QueueRow({ b }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowGrid}>
        <span className={styles.count}>{nf(b.yieldHint)}<span className={styles.est}> EST</span></span>
        <span className={styles.body}>
          {b.name}
          {b.liveNow > 0 && (
            <span className={styles.stale}> — now holds {nf(b.liveNow)} records, snapshot is stale</span>
          )}
        </span>
        <span className={styles.seg}>{b.level}</span>
        <span className={styles.seg}>{b.juris}</span>
        <Grade evidence={b.evidence} />
      </div>
      {b.note && <p className={styles.meta}>{b.note}</p>}
      {b.url && (
        /* The body's own page, which is where the evidence came from. Opened in
         * a new tab so somebody checking a row does not lose the queue. */
        <p className={styles.meta}>
          <a className={styles.link} href={b.url} target="_blank" rel="noreferrer noopener">
            {b.url}
          </a>
        </p>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * SURFACE 2 — the records with no governing body
 * ------------------------------------------------------------------------- */
function NoBody({ records, complete, loading, loadedCount }) {
  const navigate = useNavigate()
  const u = useMemo(() => unfilledBodies(records, snapshot.bodies), [records])
  const t = u.totals

  if (loading) {
    return (
      <div className="card">
        <p>
          Loading the object — {nf(loadedCount)} records so far. Nothing below is counted
          until the walk finishes, because an undercount of &ldquo;records with no
          body&rdquo; is the reassuring direction to be wrong in.
        </p>
      </div>
    )
  }

  const order = [REASONS.MINT, REASONS.MAP, REASONS.NAMED, REASONS.UNSPEC, REASONS.SILENT]

  return (
    <>
      <section className={`card ${styles.headline}`}>
        <div className={styles.big}>{nf(t.unfilled)}</div>
        <div className={styles.bigLabel}>
          records carry <b>no governing body</b>, each grouped under the label that would
          settle it — so a whole label clears in <b>one decision</b> rather than record by
          record. <b>{nf(t.MINT)}</b> of them need a new body label <b>minted</b>, which is
          Brian&rsquo;s call and not a fill.
        </div>
        {!complete && (
          <p className={styles.warn} role="alert">
            THE OBJECT IS NOT FULLY LOADED. These are undercounts. Reload before acting.
          </p>
        )}
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>It adds up, or it says so</h2>
        <table className={styles.recon}>
          <tbody>
            <tr>
              <td className={styles.n}>{nf(t.withBody)}</td>
              <td>
                a governing body is recorded. <b>Shown here as a count, not dropped:</b>{' '}
                filtering on the field that is blank is how this set went missing before.
              </td>
            </tr>
            {order.map((k) => (
              /* ⚠️ aria-disabled BECAUSE THIS IS A READOUT, NOT A CONTROL, AND
               * check-clickable is right to make that an explicit claim. A
               * reconciliation row is an arithmetic statement about the whole
               * object; there is no single record behind it to open. The rows
               * that DO carry a record get a button — see the group tables
               * below, which deep-link into the Command Center drawer. */
              <tr key={k} data-flag="1" aria-disabled="true">
                <td className={styles.n}>{nf(t[k])}</td>
                <td>
                  <b>{REASON_COPY[k].label}.</b> {REASON_COPY[k].why}{' '}
                  <i>Whose call: {REASON_COPY[k].who}.</i>
                </td>
              </tr>
            ))}
            <tr className={styles.sum}>
              <td className={styles.n}>{nf(t.accounted)}</td>
              <td>accounted for, of <b>{nf(t.universe)}</b> records loaded.</td>
            </tr>
          </tbody>
        </table>
        {u.reconciled ? (
          <p className={styles.ok}>Reconciled. {INVISIBLE_RECONCILED}</p>
        ) : (
          <p className={styles.fail} role="alert">
            <strong>{INVISIBLE_RECONCILE_FAILED}</strong> {nf(Math.abs(u.shortfall))} records
            are {u.shortfall > 0 ? 'missing from' : 'double-counted across'} the rows above.
            Do not act on them until this is explained.
          </p>
        )}
        <p className={styles.note}>
          <b>A blank field is not an absent body.</b> Every record above was probed across{' '}
          <code>sr_governing_body</code>, <code>sr_conference</code>,{' '}
          <code>sr_championship_name</code> and <code>sr_region</code> together, not one
          field at a time. Doing that is what separates the{' '}
          <b>{nf(t.SILENT)}</b> genuinely invisible records from the{' '}
          <b>{nf(t.unfilled)}</b> that merely have an empty body field — and it is the
          difference between a 3,162-record panic and a 274-record job.
        </p>
      </section>

      {order.map((reason) => {
        const groups = u.byReason(reason)
        const copy = REASON_COPY[reason]
        return (
          <section key={reason} className={`card ${styles.section}`}>
            <h2 className={styles.h2}>
              {copy.label} — {nf(groups.length)} label{groups.length === 1 ? '' : 's'},{' '}
              {nf(t[reason])} records
            </h2>
            <p className={styles.note}>{copy.why} <b>Whose call: {copy.who}.</b></p>
            {groups.length === 0 ? (
              /* ⚠️ THE REASON, NOT A BLANK. This bucket really is empty in this
               * object, and saying so in words is a different claim from a zero
               * that appeared because a query was wrong. */
              <Empty>
                Nothing is in this bucket right now. That is a measured zero, not a missing
                panel: {nf(t.universe)} records were loaded and every one of them landed in
                one of the other rows above, which is what the reconciliation proves.
              </Empty>
            ) : (
              <div className={styles.rows}>
                {groups.map((g) => (
                  <details key={g.key} className={styles.group}>
                    <summary className={styles.rowGrid}>
                      <span className={styles.count}>{nf(g.count)}</span>
                      <span className={styles.body}>{g.label}</span>
                      <span className={styles.seg}>
                        {g.states.length
                          ? `${g.states.slice(0, 4).join(', ')}${g.states.length > 4 ? ` +${g.states.length - 4}` : ''}`
                          : 'no state on any of these'}
                      </span>
                      <span className={styles.seg}>
                        {g.sports.slice(0, 2).map(([s, n]) => `${s} (${nf(n)})`).join(', ')}
                      </span>
                      <span className={styles.chev} aria-hidden="true">▸</span>
                    </summary>
                    <div className={styles.detail}>
                      <p className={styles.meta}>
                        {g.card
                          ? <>Matches the registry body <b>{g.card.name}</b> ({g.card.code}) exactly, which is why this is a fill and not a decision. </>
                          : null}
                        {g.crowned > 0
                          ? <><b>{nf(g.crowned)}</b> of these already have a champion recorded. </>
                          : 'No champion is recorded on any of these. '}
                        {g.segments.length > 1 && (
                          <>Segments: {g.segments.map(([s, n]) => `${s} (${nf(n)})`).join(', ')}. </>
                        )}
                        {g.truncated && <>Showing the first {nf(g.records.length)} of {nf(g.count)}. </>}
                      </p>
                      <table className={`${styles.table} cc-cards`}>
                        <thead>
                          <tr><th>Championship</th><th>Sport</th><th>State</th><th>Champion</th></tr>
                        </thead>
                        <tbody>
                          {g.records.map((c) => (
                            <tr key={c.id}>
                              <td data-col="name" data-label="Championship">
                                {/* The same `?record=` door MyWorklist and Body
                                  * Coverage use. A row carrying a championship's
                                  * name has an obvious thing to do with it, and a
                                  * second drawer here would be a second thing to
                                  * drift. */}
                                <button
                                  type="button"
                                  className={styles.openRecord}
                                  onClick={() => navigate(`/command-center?record=${encodeURIComponent(c.id)}`)}
                                >
                                  {c.name} →
                                </button>
                              </td>
                              <td data-label="Sport">{c.sport || <span className={styles.muted}>—</span>}</td>
                              <td data-label="State">{c.state || <span className={styles.muted}>—</span>}</td>
                              <td data-label="Champion">{c.champion || <span className={styles.muted}>not crowned</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </section>
        )
      })}
    </>
  )
}

/* ---------------------------------------------------------------------------
 * SURFACE 5 — the records nobody owns.
 *
 * Brian: "If something is unassigned - i need to know about it. we must be able
 * to assign it."
 *
 * ⚠️ IT BELONGS HERE AND NOT ON A NEW PAGE. This is the same failure the other
 * four surfaces are: a real cohort that nothing in the product could reach.
 * CommandCenter.jsx said it out loud — "THE OWNER FILTER CANNOT EXPRESS
 * 'UNASSIGNED'" — so 2,399 records (measured 2026-10-13, off the served
 * snapshot, 15,814 records) could only be found by querying the API. A
 * sixth nav entry for one more invisible set would make the sidebar an index;
 * a fifth tab on the page that exists for invisible sets is the door.
 *
 * ⚠️ AND SEEING IT IS HALF THE ASK. Every row deep-links to
 * `/command-center?record=<id>`, which opens the drawer the assign picker lives
 * in — so this surface ends in the write rather than in a number. The write
 * itself is unchanged and still a human's: a manager, a reason, a ledger row.
 * ------------------------------------------------------------------------- */
function NoRep({ records, complete, loading, loadedCount }) {
  const navigate = useNavigate()
  const u = useMemo(() => ownerlessRecords(records, ownerName), [records])
  const t = u.totals

  if (loading) {
    return (
      <div className="card">
        <p>
          Loading the object — {nf(loadedCount)} records so far. Nothing below is counted
          until the walk finishes, because an undercount of &ldquo;records with no
          rep&rdquo; is the reassuring direction to be wrong in.
        </p>
      </div>
    )
  }

  const order = [OWNER_REASONS.OPEN, OWNER_REASONS.NO_BODY, OWNER_REASONS.RETIRED]

  return (
    <>
      <section className={`card ${styles.headline}`}>
        <div className={styles.big}>{nf(t.gap)}</div>
        <div className={styles.bigLabel}>
          records have <b>no rep</b> and are real work — grouped under the governing body
          that would route them, so a whole body clears in <b>one decision</b>.{' '}
          <b>{nf(t.crowned)}</b> of the records with no rep already have a champion recorded,
          which is live revenue with nobody on it, so the groups are ordered by that
          before volume. {nf(t.RETIRED)} further records with no rep are <b>retired</b> and
          are deliberately <i>not</i> in the {nf(t.gap)}.
        </div>
        <p className={styles.note}>
          <b>{NOREP_DOOR}</b> That option did not exist until 2.107.0: the value that
          selects a record with no rep is an empty string, and the board read an empty
          string as &ldquo;no filter at all&rdquo;. Each row below also opens its record
          directly.
        </p>
        {!complete && (
          <p className={styles.warn} role="alert">
            THE OBJECT IS NOT FULLY LOADED. These are undercounts. Reload before acting.
          </p>
        )}
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>It adds up, or it says so</h2>
        <table className={styles.recon}>
          <tbody>
            <tr>
              <td className={styles.n}>{nf(t.owned)}</td>
              <td>
                a rep is recorded. <b>Shown here as a count, not dropped:</b> filtering on
                the field that is blank is how this set went missing before.
              </td>
            </tr>
            {order.map((k) => (
              /* A readout, not a control — the arithmetic is about the whole
               * object and there is no single record behind a total. The rows
               * that DO carry a record get a button, in the groups below. */
              <tr key={k} data-flag="1" aria-disabled="true">
                <td className={styles.n}>{nf(t[k])}</td>
                <td>
                  <b>{OWNER_REASON_COPY[k].label}.</b> {OWNER_REASON_COPY[k].why}{' '}
                  <i>Whose call: {OWNER_REASON_COPY[k].who}.</i>
                </td>
              </tr>
            ))}
            <tr className={styles.sum}>
              <td className={styles.n}>{nf(t.accounted)}</td>
              <td>accounted for, of <b>{nf(t.universe)}</b> records loaded.</td>
            </tr>
          </tbody>
        </table>
        {u.reconciled ? (
          <p className={styles.ok}>Reconciled. {INVISIBLE_RECONCILED}</p>
        ) : (
          <p className={styles.fail} role="alert">
            <strong>{INVISIBLE_RECONCILE_FAILED}</strong> {nf(Math.abs(u.shortfall))} records
            are {u.shortfall > 0 ? 'missing from' : 'double-counted across'} the rows above.
            Do not act on them until this is explained.
          </p>
        )}
        <p className={styles.note}>
          <b>&ldquo;Unassigned&rdquo; written in the field still counts as nobody.</b> The
          test above is <code>ownerName()</code>, the same function the board filters and
          the worklist scopes on, which folds <i>Unassigned</i>, <i>Unknown</i>,{' '}
          <i>None</i>, <i>n/a</i> and a bare dash to empty. A local re-implementation is
          how the surface and the filter would come to disagree about the same record.{' '}
          <b>{nf(t.bornOwnerless)}</b> of these still carry{' '}
          <code>sr_gap_no_owner</code> — created without a decidable rep, as opposed to
          having lost one.
        </p>
        <p className={styles.caveat}>
          <b>Why the reasons stop where they do.</b> The server&rsquo;s assignment map
          could not decide 2,318 records, split 1,949 &ldquo;no rep on the breakdown for
          this body&rdquo; / 336 &ldquo;no governing body on the record&rdquo; / 33
          retired. Only the last two are visible from a record, so only those are claimed
          here. Splitting <i>Routable now</i> into &ldquo;the map will take it&rdquo; and
          &ldquo;the sheet has no rep for this body&rdquo; would need the breakdown sheet,
          which is server-side and not in this payload — and a guessed split puts a
          confident wrong reason on 1,949 records, which sends the work to the wrong
          person.
        </p>
      </section>

      {order.map((reason) => {
        const groups = u.byReason(reason)
        const copy = OWNER_REASON_COPY[reason]
        return (
          <section key={reason} className={`card ${styles.section}`}>
            <h2 className={styles.h2}>
              {copy.label} — {nf(groups.length)} group{groups.length === 1 ? '' : 's'},{' '}
              {nf(t[reason])} records
            </h2>
            <p className={styles.note}>{copy.why} <b>Whose call: {copy.who}.</b></p>
            {groups.length === 0 ? (
              <Empty>
                Nothing is in this bucket right now. That is a measured zero, not a missing
                panel: {nf(t.universe)} records were loaded and every one of them landed in
                one of the other rows above, which is what the reconciliation proves.
              </Empty>
            ) : (
              <div className={styles.rows}>
                {groups.map((g) => (
                  <details key={g.key} className={styles.group}>
                    <summary className={styles.rowGrid}>
                      <span className={styles.count}>{nf(g.count)}</span>
                      <span className={styles.body}>{g.label}</span>
                      <span className={styles.seg}>
                        {g.states.length
                          ? `${g.states.slice(0, 4).join(', ')}${g.states.length > 4 ? ` +${g.states.length - 4}` : ''}`
                          : 'no state on any of these'}
                      </span>
                      <span className={styles.seg}>
                        {g.sports.slice(0, 2).map(([s, n]) => `${s} (${nf(n)})`).join(', ')}
                      </span>
                      <span className={styles.chev} aria-hidden="true">▸</span>
                    </summary>
                    <div className={styles.detail}>
                      <p className={styles.meta}>
                        {g.crowned > 0
                          ? <><b>{nf(g.crowned)}</b> of these already have a champion recorded — assign these first. </>
                          : 'No champion is recorded on any of these. '}
                        {g.dated === 0
                          ? 'None of them carries a date either, so nothing will ever surface them on a dated view. '
                          : <>{nf(g.dated)} carry a date. </>}
                        {g.truncated && <>Showing the first {nf(g.records.length)} of {nf(g.count)}. </>}
                      </p>
                      <table className={`${styles.table} cc-cards`}>
                        <thead>
                          <tr><th>Championship</th><th>Sport</th><th>State</th><th>Champion</th></tr>
                        </thead>
                        <tbody>
                          {g.records.map((c) => (
                            <tr key={c.id}>
                              <td data-col="name" data-label="Championship">
                                {/* The same `?record=` door the other surfaces
                                  * use — it opens the Command Center drawer,
                                  * which is where the assign picker is. One
                                  * write path, not a second one here. */}
                                <button
                                  type="button"
                                  className={styles.openRecord}
                                  onClick={() => navigate(`/command-center?record=${encodeURIComponent(c.id)}`)}
                                >
                                  {c.name} →
                                </button>
                              </td>
                              <td data-label="Sport">{c.sport || <span className={styles.muted}>—</span>}</td>
                              <td data-label="State">{c.state || <span className={styles.muted}>—</span>}</td>
                              <td data-label="Champion">{c.champion || <span className={styles.muted}>not crowned</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </section>
        )
      })}
    </>
  )
}

/* ---------------------------------------------------------------------------
 * SURFACE 3 — titles decided by STANDINGS
 * ------------------------------------------------------------------------- */
function Standings({ records, complete, loading, loadedCount }) {
  const navigate = useNavigate()
  const s = useMemo(() => standingsDecided(snapshot.bodies, records), [records])
  const t = s.totals

  if (loading) {
    return (
      <div className="card">
        <p>Loading the object — {nf(loadedCount)} records so far. Nothing is counted until the walk finishes.</p>
      </div>
    )
  }

  return (
    <>
      <section className={`card ${styles.headline}`}>
        <div className={styles.big}>{nf(t.standingsBodies)}</div>
        <div className={styles.bigLabel}>
          governing bodies crown a title by <b>STANDINGS</b> — a points championship,
          clinchable before the last race — not by a final.{' '}
          <b>{STANDINGS_IS_A_DIFFERENT_KIND}</b> It is what made the hunt refuse the GT4
          America Manufacturers title: the season had not ended, and the title had already
          been decided.
        </div>
      </section>

      <SnapshotStamp />

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>Two counts, both stated, because they disagree</h2>
        <table className={styles.recon}>
          <tbody>
            <tr>
              <td className={styles.n}>{nf(t.snapshotAliasProbe)}</td>
              <td>
                records behind these bodies per the <b>registry&rsquo;s alias probe</b>, as of{' '}
                {snapshot.meta.read_at}. This is the better number and it cannot be
                recomputed here — the alias table is not in the snapshot.
              </td>
            </tr>
            <tr data-flag="1">
              <td className={styles.n}>{nf(t.liveExactLabel)}</td>
              <td>
                records this page can reach <b>live</b>, by exact label match on{' '}
                <code>sr_governing_body</code>, <code>sr_conference</code> or{' '}
                <code>sr_region</code>. Necessarily a subset.{' '}
                <b>{nf(t.bodiesWithNoLiveMatch)}</b> of the {nf(t.standingsBodies)} bodies
                reach nothing by exact label, which is the alias gap, not an empty object.
              </td>
            </tr>
            <tr>
              <td className={styles.n}>{nf(t.liveCrowned)}</td>
              <td>of the live-reachable ones already have a champion recorded</td>
            </tr>
          </tbody>
        </table>
        <p className={styles.note}>
          <b>The difference is the alias gap and it is not averaged away.</b> Neither figure
          is &ldquo;the&rdquo; number of standings-decided records: one is a dated read by a
          stronger method, the other is a live read by a weaker one. The honest thing a
          surface can do with two disagreeing counts is show both and name which method
          produced each.
        </p>
        <p className={styles.note}>
          <b>Separately: 139 could not be reproduced.</b> The figure carried into this build
          was 139 pro records decided by standings. The registry grades{' '}
          {nf(t.standingsBodies)} bodies <code>STANDINGS</code> and{' '}
          {nf(t.finalBodies)} <code>FINAL</code>, holding {nf(t.snapshotAliasProbe)} records
          between the standings ones; the manufacturer lane&rsquo;s own date triage
          (<code>.mfg-date-triage.json</code>, generated 2026-10-04) says 165 standings of
          289 dated pro records. No artifact on this machine produces 139. It is not printed
          anywhere on this page, because a number nothing can reproduce is the kind a
          surface should refuse to carry.
        </p>
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>
          Every standings-decided body — {nf(t.standingsBodies)}
        </h2>
        <p className={styles.note}>
          Ranked by what the live feed can reach. A body showing <b>0 live</b> is showing the
          alias gap: the registry found records against it by alias and this page cannot
          match them by label. That is a reason to fix the probe, never a reason to read the
          body as empty.
        </p>
        <div className={styles.rows}>
          {s.rows.map((b) => (
            <details key={b.code} className={styles.group}>
              <summary className={styles.rowGrid}>
                <span className={styles.count}>{nf(b.liveCount)}</span>
                <span className={styles.body}>
                  {b.name}
                  <span className={styles.basis}> STANDINGS</span>
                </span>
                <span className={styles.seg}>{b.level}</span>
                <span className={styles.seg}>
                  {b.held ? `${nf(b.held)} by alias probe` : 'none by alias probe either'}
                </span>
                <Grade evidence={b.evidence} />
              </summary>
              <div className={styles.detail}>
                {b.note && <p className={styles.meta}>{b.note}</p>}
                {b.liveCount === 0 ? (
                  <Empty>
                    Nothing reaches this body by exact label in the live feed.
                    {b.held > 0
                      ? ` The registry found ${nf(b.held)} records against it by alias on ${snapshot.meta.read_at}, so this zero is the alias gap and not an absence.`
                      : ' The registry found none against it either, so this body is also on the creation queue.'}
                  </Empty>
                ) : (
                  <>
                    <p className={styles.meta}>
                      <b>{nf(b.liveCrowned)}</b> of {nf(b.liveCount)} have a champion recorded.
                      {b.truncated && ` Showing the first ${nf(b.records.length)}.`}
                    </p>
                    <table className={`${styles.table} cc-cards`}>
                      <thead>
                        <tr><th>Championship</th><th>Sport</th><th>Season</th><th>Champion</th></tr>
                      </thead>
                      <tbody>
                        {b.records.map((c) => (
                          <tr key={c.id}>
                            <td data-col="name" data-label="Championship">
                              <button
                                type="button"
                                className={styles.openRecord}
                                onClick={() => navigate(`/command-center?record=${encodeURIComponent(c.id)}`)}
                              >
                                {c.name} →
                              </button>
                            </td>
                            <td data-label="Sport">{c.sport || <span className={styles.muted}>—</span>}</td>
                            <td data-label="Season">{c.season || <span className={styles.muted}>—</span>}</td>
                            <td data-label="Champion">{c.champion || <span className={styles.muted}>not crowned</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </details>
          ))}
        </div>
      </section>
      <AliasLimit />
    </>
  )
}

/* ---------------------------------------------------------------------------
 * SURFACE 4 — the unverified registry rows
 * ------------------------------------------------------------------------- */
function Unverified() {
  const g = useMemo(() => registryGrades(snapshot.bodies), [])
  const t = g.totals

  return (
    <>
      <section className={`card ${styles.headline}`} data-unverified="1">
        <div className={styles.big}>{nf(t.knowledge)}</div>
        <div className={styles.bigLabel}>
          of the {nf(t.registry)} registry rows are graded <code>KNOWLEDGE</code> —{' '}
          <b>asserted from model knowledge and never verified against the body</b>.{' '}
          {UNVERIFIED_ON_ITS_FACE} The worst outcome on this page is somebody reading one of
          these as sourced, so the grade is the first thing in every row and there is no
          mixed list anywhere on it.
        </div>
      </section>

      <SnapshotStamp />

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>What is actually at stake</h2>
        <table className={styles.recon}>
          <tbody>
            <tr>
              <td className={styles.n}>{nf(t.verified)}</td>
              <td>
                rows graded <code>OFFICIAL</code> — read off the body&rsquo;s own page with a
                URL on the row. These are sourced.
              </td>
            </tr>
            <tr data-flag="1">
              <td className={styles.n}>{nf(t.unverified)}</td>
              <td>
                <b>not sourced.</b> {nf(t.knowledge)} graded <code>KNOWLEDGE</code> plus{' '}
                {nf(t.unverified - t.knowledge)} graded <code>OFFICIAL-COUNT</code>, where the
                body&rsquo;s published total was read and the individual title names were not.
              </td>
            </tr>
            <tr className={styles.sum}>
              <td className={styles.n}>{nf(t.registry)}</td>
              <td>rows in the registry</td>
            </tr>
          </tbody>
        </table>
        {g.reconciled ? (
          <p className={styles.ok}>Reconciled. Every row falls in exactly one grade.</p>
        ) : (
          <p className={styles.fail} role="alert">
            <strong>{INVISIBLE_RECONCILE_FAILED}</strong> A row is in no grade or two. Do not
            read the grades below as exhaustive.
          </p>
        )}
        <p className={styles.warn} role="alert">
          <b>{nf(t.knowledgeHeld)} of the unverified rows already have records against them
          — {nf(t.knowledgeHeldRecords)} records.</b> Somebody is working off these today.
          The holdings side is always MEASURED and is not affected by the grade; it is the
          universe side — what this body crowns, how many titles, under what name — that is
          unverified. Roughly {nf(t.estTitlesBehindKnowledge)} estimated titles sit behind the
          unverified rows.
        </p>
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>Unverified rows by level</h2>
        <table className={styles.recon}>
          <tbody>
            {g.byLevel.map((r) => (
              /* A readout: a count of registry rows at one level. Nothing to open. */
              <tr key={r.level} aria-disabled="true">
                <td className={styles.n}>{nf(r.count)}</td>
                <td>{r.level} — {nf(r.held)} records held against them</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className={`card ${styles.section}`} data-unverified="1">
        <h2 className={styles.h2}>
          The work list — {nf(t.knowledge)} unverified rows
        </h2>
        <p className={styles.note}>
          Ranked by records already held, because an unverified row somebody is already
          working is worth checking before one nobody has touched. Verifying a row means
          reading the body&rsquo;s own page and putting the URL on it.
        </p>
        <div className={styles.rows}>
          {g.rows.map((b) => (
            /* ⚠️ A REGISTRY BODY IS NOT A RECORD, SO THERE IS NOTHING TO OPEN,
             * and that is declared rather than left for somebody to discover by
             * tapping. The row's own fix is to go and read the body's page and
             * put the URL on it — work that happens outside this product, which
             * is exactly why the surface can only list it. */
            <div key={b.code} className={styles.row} data-unverified="1" aria-disabled="true">
              <div className={styles.rowGrid}>
                <span className={styles.count}>{nf(b.held)}</span>
                <span className={styles.body}>{b.name}</span>
                <span className={styles.seg}>{b.level}</span>
                <span className={styles.seg}>{b.juris}</span>
                <Grade evidence={b.evidence} />
              </div>
              <p className={styles.meta}>
                <b>UNVERIFIED.</b> {GRADES.KNOWLEDGE.why}
                {b.yieldHint ? ` Estimated ${nf(b.yieldHint)} titles — an estimate, not a holding.` : ''}
                {b.note ? ` ${b.note}` : ''}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  )
}

/* ---------------------------------------------------------------------------
 * The page
 * ------------------------------------------------------------------------- */
export default function InvisibleRecords() {
  const { surface } = useParams()
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  const { data, status, complete, loadedCount, error } = useChampionships()

  const active = SURFACES.some((s) => s.id === surface) ? surface : SURFACES[0].id
  const loading = status === 'loading'

  if (!allowed) {
    /* RBAC: unmapped is denied. The page says so in the voice of the grant
     * rather than rendering an empty board, which reads as a bug. */
    return (
      <>
        <PageHeader eyebrow="Reference" title="Invisible Records" />
        <div className="card">
          <p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p>
        </div>
      </>
    )
  }

  const records = data || []

  return (
    <>
      <PageHeader
        eyebrow="Reference"
        title="Invisible Records"
        lead={`Five sets of records nothing in the product could reach. ${
          INVISIBLE_HEADLINE[0].toUpperCase()}${INVISIBLE_HEADLINE.slice(1)
        }, so each one is a surface here instead. Read-only: nothing on this page writes to the object.`}
      />

      {/* THE DOORS. Real links, so a surface can be sent to somebody. */}
      <nav className={styles.tabs} aria-label="Invisible record sets">
        {SURFACES.map((s) => (
          <NavLink
            key={s.id}
            to={`/invisible-records/${s.id}`}
            className={({ isActive }) => `${styles.tab}${isActive || s.id === active ? ` ${styles.tabOn}` : ''}`}
          >
            {s.label}
          </NavLink>
        ))}
      </nav>

      {status === 'error' && (
        /* ⚠️ DEGRADE HONESTLY. An unreachable source is not a zero. */
        <p className={styles.fail} role="alert">
          <strong>The championship feed could not be loaded.</strong> {error || 'No reason was returned.'}{' '}
          The two registry surfaces below still render, because they read a dated snapshot
          rather than the live feed — but every live count on this page is unavailable, not
          zero, until this is fixed.
        </p>
      )}

      {active === 'creation-queue' && <CreationQueue records={records} complete={complete} />}
      {active === 'no-body' && (
        <NoBody records={records} complete={complete} loading={loading} loadedCount={loadedCount} />
      )}
      {active === 'no-rep' && (
        <NoRep records={records} complete={complete} loading={loading} loadedCount={loadedCount} />
      )}
      {active === 'standings' && (
        <Standings records={records} complete={complete} loading={loading} loadedCount={loadedCount} />
      )}
      {active === 'unverified' && <Unverified />}
    </>
  )
}
